/**
 * Accept an invitation — the five states `auth-invite.html` renders.
 *
 * Visual Reference: `design/screens/auth-invite.html`. Its own notes carry what
 * each state is for:
 *
 *   default   "the summary says who invited them, what they are joining and
 *              what a Reviewer may do, in words someone who has never seen
 *              Leadgible can act on"
 *   existing  "the same summary, no password field, and a heading that names
 *              the one decision left. Nothing asks them to prove who they are
 *              twice."
 *   expired   "it names Dana, says how long invitations last, and reassures
 *              that their place on the team is unaffected. The way out is to
 *              contact the person, not support."
 *   accepted  "states the membership and the role rather than an error, and
 *              sends them somewhere useful."
 *
 * WHICH STATE IS THE SERVER'S CALL, not this component's. `GET
 * /v1/auth/invitation/{id}` returns it, because expiry and acceptance are facts
 * about a row and a clock the browser does not have.
 *
 * The card is centred like every other in this family — DECISION-039 — and the
 * two message states carry `48px 28px` at gap 14, the shape reset's expired
 * card uses.
 *
 * Story 3.9.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { PASSWORD_MIN_LENGTH } from '@leadgible/schema'
import { AuthCard, AuthLink, CardHeading, CardMark } from './AuthPanel'
import { Button } from '../ui/Button'
import { Callout } from '../ui/Callout'
import { Field } from '../ui/Field'
import { apiGet, apiPost, ApiFailure } from '../lib/api'

type Invitation = {
  id: string
  state: 'pending' | 'expired' | 'accepted'
  email: string
  inviterName: string
  inviterEmail: string
  organisationName: string
  role: string
  roleLabel: string
  roleExplainer: string
  hasAccount: boolean
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name

const initials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

export function AcceptInvitation() {
  const id = new URLSearchParams(window.location.search).get('id') ?? ''
  const [invitation, setInvitation] = useState<Invitation | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!id) {
      setLoadError("That link is missing its invitation. Ask whoever invited you to send a new one.")
      return
    }
    let live = true
    void (async () => {
      try {
        const loaded = await apiGet<Invitation>(`/v1/auth/invitation/${encodeURIComponent(id)}`)
        if (live) setInvitation(loaded)
      } catch (error) {
        if (live) {
          setLoadError(
            error instanceof ApiFailure
              ? error.message
              : "We couldn't reach Leadgible. Check your connection and reload.",
          )
        }
      }
    })()
    return () => {
      live = false
    }
  }, [id])

  async function accept(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setFailure(null)
    try {
      await apiPost('/v1/auth/accept-invitation', {
        invitationId: id,
        ...(invitation?.hasAccount ? {} : { name, password }),
      })
      // A new invitee arrives signed in; an existing one is sent to log in,
      // because they were never asked to prove themselves here.
      window.location.assign(invitation?.hasAccount ? '/login' : '/dashboard')
    } catch (error) {
      const failed = error instanceof ApiFailure ? error : null
      setFailure(failed)
      // A seat refusal, an expiry or a race is a different CARD, not an error
      // above a form. The server has already decided which; re-reading is how
      // the screen finds out.
      if (failed && (failed.status === 409 || failed.status === 404)) {
        try {
          setInvitation(await apiGet<Invitation>(`/v1/auth/invitation/${encodeURIComponent(id)}`))
        } catch {
          /* Keep the message that is already on screen. */
        }
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (loadError) {
    return (
      <AuthCard gap="gap-mdx-14" pad="px-mdx-28 py-mdx-48">
        <CardMark tone="error">!</CardMark>
        <h1 className="m-0 text-mdx-20 font-mdx-bold leading-mdx-heading tracking-mdx-h1">
          That invitation link isn't valid
        </h1>
        <p className="m-0 max-w-mdx-measure-44 text-mdx-16 leading-mdx-body">{loadError}</p>
      </AuthCard>
    )
  }

  if (!invitation) {
    return (
      <AuthCard gap="gap-mdx-14" pad="px-mdx-28 py-mdx-48">
        <p className="m-0 text-mdx-16 text-mdx-slate">Opening your invitation…</p>
      </AuthCard>
    )
  }

  if (invitation.state === 'expired') {
    return (
      <AuthCard gap="gap-mdx-14" pad="px-mdx-28 py-mdx-48">
        <CardMark tone="error">!</CardMark>
        <h1 className="m-0 text-mdx-20 font-mdx-bold leading-mdx-heading tracking-mdx-h1">
          This invitation has expired. Ask {firstName(invitation.inviterName)} to send a new one.
        </h1>
        <p className="m-0 max-w-mdx-measure-44 text-mdx-16 leading-mdx-body">
          Invitations last seven days. {invitation.inviterName} can send another from Members and
          roles, and your place on the team is unaffected.
        </p>
        <AuthLink href={`mailto:${invitation.inviterEmail}`}>
          Email {invitation.inviterName}
        </AuthLink>
      </AuthCard>
    )
  }

  if (invitation.state === 'accepted') {
    return (
      <AuthCard gap="gap-mdx-14" pad="px-mdx-28 py-mdx-48">
        <CardMark tone="hot">&#10003;</CardMark>
        <h1 className="m-0 text-mdx-17 font-mdx-semibold leading-mdx-heading">
          You're already a member
        </h1>
        <p className="m-0 max-w-mdx-measure-44 text-mdx-16 leading-mdx-body">
          You joined {invitation.organisationName} as a {invitation.roleLabel}. This invitation has
          nothing left to do.
        </p>
        <AuthLink href="/dashboard">Go to your dashboard</AuthLink>
      </AuthCard>
    )
  }

  return (
    <AuthCard align="left" gap="gap-mdx-24">
      {/*
        The summary, on `--mdx-ground` at radius `--mdx-radius-control` with
        `20px 16px` — the one block in the auth family that is centred inside a
        left-aligned card, because it is a statement rather than a field.
      */}
      <div className="flex flex-col items-center gap-mdx-14 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-20 text-center">
        <span
          aria-hidden="true"
          className="flex size-mdx-avatar-lg shrink-0 items-center justify-center rounded-mdx-pill bg-mdx-brand text-mdx-14 font-mdx-bold text-mdx-white"
        >
          {initials(invitation.inviterName)}
        </span>
        <span className="flex min-w-0 flex-col gap-mdx-6">
          <span className="text-mdx-16">
            <span className="font-mdx-semibold">{invitation.inviterName}</span> invited you to join{' '}
            <span className="font-mdx-semibold">{invitation.organisationName}</span> as a{' '}
            <span className="font-mdx-semibold">{invitation.roleLabel}</span>.
          </span>
          <span className="text-mdx-15 text-mdx-slate">{invitation.roleExplainer}</span>
        </span>
      </div>

      {/* `existing` names the one decision left; a new invitee has two fields. */}
      {invitation.hasAccount ? (
        <CardHeading title={`Join ${invitation.organisationName}`} />
      ) : null}

      <form className="flex flex-col gap-mdx-20" onSubmit={accept} noValidate>
        {failure ? <Callout>{failure.message}</Callout> : null}

        {invitation.hasAccount ? null : (
          <>
            <Field
              label="Full name"
              type="text"
              name="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Field
              label="Password"
              type="password"
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              helper={`Use ${PASSWORD_MIN_LENGTH} characters or more. A short phrase is stronger than a scramble.`}
            />
          </>
        )}

        <Button
          type="submit"
          full
          disabled={!invitation.hasAccount && password.length < PASSWORD_MIN_LENGTH}
          loading={submitting ? `Joining ${invitation.organisationName}` : undefined}
        >
          Accept and join
        </Button>
        {/* Centred in its own paragraph, as the mockup renders it. */}
        <p className="m-0 text-center text-mdx-15">
          <AuthLink href="/login">Decline</AuthLink>
        </p>
      </form>
    </AuthCard>
  )
}
